
<div class="container-fluid">
  @if (loading) {
    <div  class="spinner-border" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
  }

  @if (error) {
    <div class="alert alert-danger">
      <div class="row">
        <h4>Error loading table</h4>
        <p>{{error}}</p>
      </div>
    </div>
  }

  @if (!loading && !error) {
    <h2 class="mt-5">Datasets available for species <span class="fst-italic">{{species}}</span></h2>
    <div class="container">
      <mat-table #datasetInfoTable [dataSource]="datasetDescriptions">
        <ng-container matColumnDef="name">
          <mat-header-cell *matHeaderCellDef style="width: 30% !important; flex: 0 0 30% !important;">
            Name
          </mat-header-cell>
          <mat-cell *matCellDef="let desc" style="width: 30% !important; flex: 0 0 30% !important;">{{desc.dataset}}</mat-cell>
        </ng-container>
        <ng-container matColumnDef="desc">
          <mat-header-cell *matHeaderCellDef>
            Description
          </mat-header-cell>
          <mat-cell *matCellDef="let desc">{{desc.description}}</mat-cell>
        </ng-container>
        <ng-container matColumnDef="updated">
          <mat-header-cell *matHeaderCellDef>
            Updated
          </mat-header-cell>
          <mat-cell *matCellDef="let desc">{{desc.created}}</mat-cell>
        </ng-container>
        <mat-header-row *matHeaderRowDef="['name', 'desc', 'updated']; sticky: true " class="mat-elevation-z1"></mat-header-row>
        <mat-row *matRowDef="let row; columns: ['name', 'desc', 'updated']" ></mat-row>
      </mat-table>
    </div>
  }

  @if (!loading && !error && datasetInfo) {
    <h2 class="mt-5">Summary of dataset {{dataset}}: {{datasetInfo['total_samples']}} samples from {{datasetInfo['total_subjects']}} subjects</h2>
    <p class="fst-italic">{{datasetInfo['description']}}</p>
    <div class="row">
      <div class="col-lg-3 col-md-6 col-sm-12">
        <google-chart
          [type]="barChartType"
          [title]="'Sex (no. of individuals)'"
          [data]="datasetInfo['sex_count']"
          [options]="{legend: {position: 'none'}}"
          >
        </google-chart>
      </div>
      <div class="col-lg-3 col-md-6 col-sm-12">
        <google-chart
          [type]="pieChartType"
          [title]="'Condition (% individuals)'"
          [data]="datasetInfo['condition_count']"
          [options]="{chartArea: {left: 0, width:'100%', height: '75%'}}"
          [width]="500"
          >
        </google-chart>
      </div>
      <div class="col-lg-3 col-md-6 col-sm-12">
        <google-chart
          [type]="pieChartType"
          [title]="'Cell Type (% samples)'"
          [data]="datasetInfo['celltype_count']"
          [options]="{chartArea: {left: 0, width:'100%', height: '75%'}}"
          [width]="500"
          >
        </google-chart>
      </div>
      <div class="col-lg-3 col-md-6 col-sm-12">
        <google-chart
          [type]="pieChartType"
          [title]="'Tissue (% samples)'"
          [data]="datasetInfo['tissue_count']"
          [options]="{chartArea: {left: 0, width:'100%', height: '75%'}}"
          [width]="500"
          >
        </google-chart>
      </div>
    </div>
  }

  @if (!loading && !error && datasetInfo) {
    <div class="container">
      <h2 class="mt-5">Studies included in {{dataset}}:</h2>
      <p>Samples may be omitted from VDJbase if the associated set of reads is too small for analysis. </p>
      <p>Clicking on the Download Repertoires link will download a script that you can use to download annotated repertoires for all samples in the study.
        Please note that the server has a download limit, to prevent abuse. If the HTTP code 503 is returned, the server is rate-limited and it will provide an indication of when you can retry.
      </p>
      <mat-table #datasetStudyTable [dataSource]="datasetInfo['studies']">
        <ng-container matColumnDef="name">
          <mat-header-cell *matHeaderCellDef class="explore-small-col">
            Project ID
          </mat-header-cell>
          <mat-cell *matCellDef="let study"  class="explore-small-col">{{study.study_name}}</mat-cell>
        </ng-container>
        <ng-container matColumnDef="accession_id">
          <mat-header-cell *matHeaderCellDef class="explore-small-col">
            Accession ID
          </mat-header-cell>
          <mat-cell class="explore-small-col" *matCellDef="let study">
            @if (study.study_id) {
              @for (study_id of study.study_id.split(','); track study_id) {
                @if (study_id.includes('http')) {
                  <a href={{study_id}} title="View study details" rel="noopener"><i class="fa fa-external-link" style="line-height: inherit;"></i></a>
                }
                @if (!study_id.includes('http')) {
                  <span> {{study_id}}</span>
                }
              }
            }
          </mat-cell>
        </ng-container>
        <ng-container matColumnDef="institute">
          <mat-header-cell *matHeaderCellDef  class="explore-large-col header-flex-tight">
            Lab
          </mat-header-cell>
          <mat-cell  class="explore-large-col" *matCellDef="let study">{{study.lab_address}}</mat-cell>
        </ng-container>
        <ng-container matColumnDef="num_subjects">
          <mat-header-cell *matHeaderCellDef  class="explore-small-col header-flex-tight">
            Subjects
          </mat-header-cell>
          <mat-cell *matCellDef="let study"  class="explore-small-col header-flex-tight">{{study.num_subjects}}</mat-cell>
        </ng-container>
        <ng-container matColumnDef="subjects_in_vdjbase">
          <mat-header-cell *matHeaderCellDef  class="explore-small-col header-flex-tight">
            Subjects in VDJbase
          </mat-header-cell>
          <mat-cell *matCellDef="let study"  class="explore-small-col header-flex-tight">{{study.subjects_in_vdjbase}}</mat-cell>
        </ng-container>
        <ng-container matColumnDef="num_samples">
          <mat-header-cell *matHeaderCellDef  class="explore-small-col header-flex-tight">
            Samples
          </mat-header-cell>
          <mat-cell *matCellDef="let study"  class="explore-small-col header-flex-tight">{{study.num_samples}}</mat-cell>
        </ng-container>
        <ng-container matColumnDef="samples_in_vdjbase">
          <mat-header-cell *matHeaderCellDef  class="explore-small-col header-flex-tight">
            Samples in VDJbase
          </mat-header-cell>
          <mat-cell *matCellDef="let study"  class="explore-small-col header-flex-tight">{{study.samples_in_vdjbase}}</mat-cell>
        </ng-container>
        <ng-container matColumnDef="paper" >
          <mat-header-cell *matHeaderCellDef  class="explore-small-col header-flex-tight">
            Paper
          </mat-header-cell>
          <mat-cell *matCellDef="let study"  class="explore-small-col header-flex-tight">
            @if (study.pub_ids) {
              @for (pub_id of study.pub_ids.split(','); track pub_id) {
                @if (pub_id.includes('http')) {
                  <a href={{pub_id}} title="View publication" rel="noopener"><i class="fa fa-external-link" style="line-height: inherit;"></i></a>
                }
                @if (!pub_id.includes('http')) {
                  <span> {{pub_id}}</span>
                }
              }
            }
          </mat-cell>
        </ng-container>
        <ng-container matColumnDef="repertoires">
          <mat-header-cell *matHeaderCellDef  class="explore-small-col header-flex-tight">
            Download Repertoires
          </mat-header-cell>
          <mat-cell *matCellDef="let study"  class="explore-small-col header-flex-tight">
            @if (study.repertoires) {
              <a href={{apibasePath}}{{study.repertoires}} title="Download repertoires" rel="noopener"><i class="fa fa-external-link" style="line-height: inherit;"></i></a>
            }
          </mat-cell>
        </ng-container>
        <mat-header-row *matHeaderRowDef="['name', 'accession_id', 'institute', 'num_subjects', 'subjects_in_vdjbase', 'num_samples', 'samples_in_vdjbase', 'paper', 'repertoires']; sticky: true " class="mat-elevation-z1"></mat-header-row>
        <mat-row *matRowDef="let row; columns: ['name', 'accession_id', 'institute', 'num_subjects', 'subjects_in_vdjbase', 'num_samples', 'samples_in_vdjbase', 'paper', 'repertoires']" ></mat-row>
      </mat-table>
    </div>
  }

  @if (!loading && !error && !datasetInfo) {
    <div class="container">
      <p>Please select a single dataset to see its statistics and composition</p>
    </div>
  }
</div>
